import React, { useEffect, useState } from 'react';
import { API, showError, showSuccess } from '../helpers';
import { renderQuota } from '../helpers/render';
import { Button, Space, Modal, Table, Typography } from "@douyinfe/semi-ui";
import Title from "@douyinfe/semi-ui/lib/es/typography/title";
import { IconPlus, IconDelete, IconEdit } from '@douyinfe/semi-icons';

const TopUpPackageSettings = () => {
    const [packages, setPackages] = useState([]);
    const [editingPackage, setEditingPackage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // 表单状态
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        quota: 0,
        price: 0,
        discount: ''
    });

    // 加载套餐列表
    const loadPackages = async () => {
        try {
            const res = await API.get('/api/package/');
            const { success, data } = res.data;
            if (success && data) {
                setPackages(data);
            }
        } catch (err) {
            showError('加载充值套餐失败');
        }
    };

    useEffect(() => {
        loadPackages();
    }, []);

    // 打开编辑弹窗
    const openEditModal = (pkg = null) => {
        if (pkg) {
            setFormData({
                id: pkg.id,
                name: pkg.name,
                quota: pkg.quota,
                price: pkg.price,
                discount: pkg.discount || ''
            });
        } else {
            // 新增套餐
            setFormData({
                id: Date.now(), // 使用时间戳作为临时ID
                name: '',
                quota: 0,
                price: 0,
                discount: ''
            });
        }
        setShowModal(true);
    };

    // 删除套餐
    const deletePackage = async (pkg) => {
        if (packages.length <= 1) {
            showError('至少保留一个套餐');
            return;
        }

        const confirmed = window.confirm(`确定要删除套餐"${pkg.name}"吗？`);
        if (!confirmed) return;

        const newPackages = packages.filter(p => p.id !== pkg.id);
        await savePackages(newPackages);
    };

    // 保存套餐
    const savePackages = async (newPackages) => {
        setLoading(true);
        try {
            const res = await API.put('/api/package/', {
                packages: newPackages
            });
            const { success, message } = res.data;
            if (success) {
                showSuccess('保存成功');
                setPackages(newPackages);
                setShowModal(false);
            } else {
                showError(message || '保存失败');
            }
        } catch (err) {
            showError('保存失败');
        } finally {
            setLoading(false);
        }
    };

    // 提交表单
    const handleSubmit = () => {
        // 验证
        if (!formData.name.trim()) {
            showError('套餐名称不能为空');
            return;
        }
        if (formData.quota <= 0) {
            showError('额度必须大于0');
            return;
        }
        if (formData.price < 0) {
            showError('价格不能为负数');
            return;
        }

        // 检查是否编辑或新增
        let newPackages;
        if (editingPackage) {
            // 编辑
            newPackages = packages.map(p => 
                p.id === editingPackage.id ? { ...formData } : p
            );
        } else {
            // 新增
            newPackages = [...packages, { ...formData }];
        }

        savePackages(newPackages);
    };

    // 更新套餐数据
    const updatePackageData = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 列定义
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 80
        },
        {
            title: '套餐名称',
            dataIndex: 'name',
            width: 120
        },
        {
            title: '额度',
            dataIndex: 'quota',
            width: 150,
            render: (quota) => renderQuota(quota)
        },
        {
            title: '价格(元)',
            dataIndex: 'price',
            width: 100,
            render: (price) => `¥${price}`
        },
        {
            title: '折扣标签',
            dataIndex: 'discount',
            width: 100
        },
        {
            title: '操作',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        theme="light"
                        size="small"
                        icon={<IconEdit />}
                        onClick={() => {
                            setEditingPackage(record);
                            openEditModal(record);
                        }}
                    >
                        编辑
                    </Button>
                    <Button
                        type="danger"
                        theme="light"
                        size="small"
                        icon={<IconDelete />}
                        onClick={() => deletePackage(record)}
                    >
                        删除
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>套餐列表</Title>
                <Space>
                    <Button 
                        type="primary" 
                        theme="solid"
                        icon={<IconPlus />}
                        onClick={() => {
                            setEditingPackage(null);
                            openEditModal();
                        }}
                        style={{ borderRadius: '8px' }}
                    >
                        新增套餐
                    </Button>
                    <Button 
                        type="warning" 
                        theme="solid"
                        onClick={loadPackages}
                        style={{ borderRadius: '8px' }}
                    >
                        刷新列表
                    </Button>
                </Space>
            </div>

            <Table 
                columns={columns} 
                dataSource={packages} 
                pagination={false}
                style={{ marginTop: 16 }}
            />

            <div style={{ 
                marginTop: 24,
                padding: 16,
                borderRadius: 8,
                border: '1px solid #e4e7ed',
                background: '#f8f9fa'
            }}>
                <div style={{ color: '#606266', fontSize: '14px' }}>
                    <strong>💡 使用说明：</strong>
                    <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
                        <li>套餐名称：显示在充值页面的套餐卡片上</li>
                        <li>额度：用户充值后获得的 token 额度</li>
                        <li>价格：用户需要支付的金额（单位：元）</li>
                        <li>折扣标签：可选，用于显示促销信息（如"8折"、"限时优惠"）</li>
                        <li>至少保留一个套餐，确保用户可以充值</li>
                    </ul>
                </div>
            </div>

            {/* 编辑/新增弹窗 */}
            <Modal
                title={editingPackage ? "编辑套餐" : "新增套餐"}
                visible={showModal}
                onCancel={() => setShowModal(false)}
                footer={
                    <Space>
                        <Button onClick={() => setShowModal(false)}>取消</Button>
                        <Button 
                            type="primary" 
                            theme="solid"
                            loading={loading}
                            onClick={handleSubmit}
                        >
                            保存
                        </Button>
                    </Space>
                }
                centered
                width={500}
            >
                <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                        <Typography.Text>套餐名称</Typography.Text>
                        <input
                            type="text"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                marginTop: 4,
                                border: '1px solid #dcdfe6',
                                borderRadius: 4,
                                fontSize: 14
                            }}
                            placeholder="例如：体验版、标准版、专业版"
                            value={formData.name}
                            onChange={(e) => updatePackageData('name', e.target.value)}
                        />
                    </div>
            
                    <div style={{ marginBottom: 16 }}>
                        <Typography.Text>额度</Typography.Text>
                        <input
                            type="number"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                marginTop: 4,
                                border: '1px solid #dcdfe6',
                                borderRadius: 4,
                                fontSize: 14
                            }}
                            placeholder="例如：1000000"
                            value={formData.quota}
                            onChange={(e) => updatePackageData('quota', parseInt(e.target.value) || 0)}
                        />
                        <div style={{ color: '#909399', fontSize: '12px', marginTop: 4 }}>
                            额度必须大于0，建议使用整数，如：1000000
                        </div>
                    </div>
            
                    <div style={{ marginBottom: 16 }}>
                        <Typography.Text>价格（元）</Typography.Text>
                        <input
                            type="number"
                            step="0.01"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                marginTop: 4,
                                border: '1px solid #dcdfe6',
                                borderRadius: 4,
                                fontSize: 14
                            }}
                            placeholder="例如：9.9"
                            value={formData.price}
                            onChange={(e) => updatePackageData('price', parseFloat(e.target.value) || 0)}
                        />
                        <div style={{ color: '#909399', fontSize: '12px', marginTop: 4 }}>
                            价格不能为负数，支持小数，如：9.9、39.9、299.9
                        </div>
                    </div>
            
                    <div style={{ marginBottom: 16 }}>
                        <Typography.Text>折扣标签（可选）</Typography.Text>
                        <input
                            type="text"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                marginTop: 4,
                                border: '1px solid #dcdfe6',
                                borderRadius: 4,
                                fontSize: 14
                            }}
                            placeholder="例如：8折、7折、限时优惠"
                            value={formData.discount}
                            onChange={(e) => updatePackageData('discount', e.target.value)}
                        />
                        <div style={{ color: '#909399', fontSize: '12px', marginTop: 4 }}>
                            可输入折扣信息，如为空则不显示折扣标签
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TopUpPackageSettings;
